import React, { useState, useCallback, useRef, useContext, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet";
import classes from "./AIExtractionStudio.module.scss";
import { Typography, CircularProgress, Tooltip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { DDD, E2Data } from "../lib/ddd/ddd";
import { OrgIdContext } from "../store/orgId_context";

const apiRequestAddress =
  process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";

// TODO: remove once GEI.CreateStandardFromSpecific supports FileBase64 directly
const fileEndpoint =
  process.env.REACT_APP_FILE_ENDPOINT || window.location.origin + "/File";

const uploadFileToServer = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(fileEndpoint, form, {
    headers: { "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
  if (res?.data?.Result && res?.data?.Status?.toUpperCase() === "OK")
    return res.data.Result.Id;
  throw new Error("File upload failed");
};

const getDocupathMode = async () => {
  try {
    const ddd = new DDD(apiRequestAddress);
    const ret = await ddd.Rpc_ExecuteMethod("EUeInvoices.GetSettingValue", {"VarName" : "DocupathMode"});
    if (typeof ret?.result?.Result === "string") return ret.result.Result;
  } catch {}
  return "dev";
};

const extractFileOld = async (file, mode = "dev") => {
  // TODO: swap uploadFileToServer for fileToBase64 and pass FileBase64 directly once API supports it
  const fileId = await uploadFileToServer(file);
  const ddd = new DDD(apiRequestAddress);
  const response = await ddd.Rpc_ExecuteMethod("GEI.CreateStandardFromSpecific", {
    Version: "Docupath",
    Legislation: "EU",
    SpecObj: null,
    Extraction: true,
    FileId: fileId,
    WriteFile: true,
    Mode: mode,
  });
  return response;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const extractFileNew = async (file) => {
  const fileBase64 = await fileToBase64(file);
  const ddd = new DDD(apiRequestAddress);
  return await ddd.Rpc_ExecuteMethod("DDDI.ExtractStdInpObj", { FileBase64: fileBase64 });
};

const saveInvoice = async (object, connectionKey, steps = []) => {
  const ddd = DDD.WithConnectionKey(apiRequestAddress, connectionKey);
  return await ddd.Rpc_ExecuteMethod("EUeInvoices.DDDI_Save", {
    GEI: 2,
    Complexity: "Normal",
    Object: object,
    ...(steps.length > 0 && { Steps: steps }),
  });
};

const STATUS = { IDLE: "idle", LOADING: "loading", SUCCESS: "success", ERROR: "error" };

// ── StepPicker ────────────────────────────────────────────────────────────────

const StepPicker = ({ steps, selected, onToggle }) => (
  <div className={classes.stepPicker}>
    {steps.map((step) => {
      const disabled = step.enabled === false;
      const tooltipTitle = disabled && step.enabledMessage
        ? step.enabledMessage
        : step.StepUIDescription || step.StepDescription || "";
      return (
        <Tooltip key={step.Id} title={tooltipTitle} arrow placement="top">
          <div
            className={`${classes.stepBox} ${selected.includes(step.Step) ? classes.stepBoxSelected : ""} ${disabled ? classes.stepBoxDisabled : ""}`}
            onClick={() => !disabled && onToggle(step.Step)}
          >
            {step.Step}
          </div>
        </Tooltip>
      );
    })}
  </div>
);

// ── StepsConfig ───────────────────────────────────────────────────────────────

const STEP_MODES = [
  { key: "none", label: "None" },
  { key: "all", label: "Same for all" },
  { key: "individual", label: "Per invoice" },
];

const StepsConfig = ({ steps, stepsLoading, mode, onModeChange, selectedSteps, onStepToggle }) => (
  <div className={classes.stepsConfig}>
    <Typography variant="caption" className={classes.stepsLabel}>
      Workflow steps
    </Typography>
    <div className={classes.stepsModes}>
      {STEP_MODES.map(({ key, label }) => (
        <button
          key={key}
          className={`${classes.modeBtn} ${mode === key ? classes.modeBtnActive : ""}`}
          onClick={() => onModeChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
    {mode === "all" && (
      stepsLoading ? (
        <CircularProgress size={16} thickness={5} style={{ margin: "0.5rem 0" }} />
      ) : steps.length > 0 ? (
        <StepPicker steps={steps} selected={selectedSteps} onToggle={onStepToggle} />
      ) : (
        <Typography variant="caption" className={classes.stepsHint}>No steps available.</Typography>
      )
    )}
    {mode === "individual" && (
      <Typography variant="caption" className={classes.stepsHint}>
        Steps will be selected per invoice after extraction.
      </Typography>
    )}
  </div>
);

// ── ExtractionModeToggle ───────────────────────────────────────────────────────

const ExtractionModeToggle = ({ mode, onChange, canSwitch, loading }) => {
  const disabled = loading || !canSwitch;
  const tooltipTitle = loading
    ? "Loading extraction settings…"
    : !canSwitch
      ? "Only one extraction engine is enabled for your account"
      : "";
  return (
    <Tooltip title={tooltipTitle} arrow placement="left">
      <div className={`${classes.extractionToggle} ${disabled ? classes.extractionToggleDisabled : ""}`}>
        <button
          type="button"
          className={`${classes.extractionToggleBtn} ${mode === "old" ? classes.extractionToggleBtnActive : ""}`}
          onClick={() => !disabled && onChange("old")}
          disabled={disabled}
        >
          Old
        </button>
        <button
          type="button"
          className={`${classes.extractionToggleBtn} ${mode === "new" ? classes.extractionToggleBtnActive : ""}`}
          onClick={() => !disabled && onChange("new")}
          disabled={disabled}
        >
          New
        </button>
      </div>
    </Tooltip>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AIExtractionStudio = () => {
  const { connectionKey, mainOrgCountry, isMaintainer } = useContext(OrgIdContext);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [stepsMode, setStepsMode] = useState(() => localStorage.getItem("aiStudio_stepsMode") || "none");
  const [globalSteps, setGlobalSteps] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aiStudio_globalSteps")) || []; } catch { return []; }
  });
  const [perInvoiceSteps, setPerInvoiceSteps] = useState({});
  const [awaitingStepConfirm, setAwaitingStepConfirm] = useState(false);
  const [objectEdits, setObjectEdits] = useState({});
  const [objectEditErrors, setObjectEditErrors] = useState({});
  const phase1Ref = useRef(null);
  const inputRef = useRef(null);
  const previewObjUrl = useRef(null);
  const previewHideTimer = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPos, setPreviewPos] = useState({ top: 0, left: 0 });

  useEffect(() => { localStorage.setItem("aiStudio_stepsMode", stepsMode); }, [stepsMode]);
  useEffect(() => { localStorage.setItem("aiStudio_globalSteps", JSON.stringify(globalSteps)); }, [globalSteps]);
  useEffect(() => () => {
    if (previewObjUrl.current) URL.revokeObjectURL(previewObjUrl.current);
    if (previewHideTimer.current) clearTimeout(previewHideTimer.current);
  }, []);

  const showPreview = useCallback((e, file) => {
    if (previewHideTimer.current) clearTimeout(previewHideTimer.current);
    if (previewObjUrl.current) URL.revokeObjectURL(previewObjUrl.current);
    const url = URL.createObjectURL(file);
    previewObjUrl.current = url;
    const rect = e.currentTarget.getBoundingClientRect();
    const PREVIEW_W = Math.min(Math.max(window.innerWidth * 0.5, 340), 820);
    const PREVIEW_H = window.innerHeight * 0.8;
    let left = rect.right + 12;
    if (left + PREVIEW_W > window.innerWidth - 8) left = rect.left - PREVIEW_W - 12;
    let top = rect.top;
    if (top + PREVIEW_H > window.innerHeight - 8) top = window.innerHeight - PREVIEW_H - 8;
    setPreviewPos({ top, left });
    setPreviewUrl(url);
  }, []);

  const hidePreview = useCallback(() => {
    previewHideTimer.current = setTimeout(() => {
      setPreviewUrl(null);
      if (previewObjUrl.current) {
        URL.revokeObjectURL(previewObjUrl.current);
        previewObjUrl.current = null;
      }
    }, 120);
  }, []);

  const cancelHidePreview = useCallback(() => {
    if (previewHideTimer.current) clearTimeout(previewHideTimer.current);
  }, []);

  const stepsQueryInstance = useMemo(() => {
    if (!connectionKey) return null;
    return E2Data.WithConnectionKey(apiRequestAddress, connectionKey, "EU_WorkflowSteps")
      .ApplyParams({ filter: "UserAccessible=true", orderBy: "Step" });
  }, [connectionKey]);

  const scrQueryInstance = useMemo(() => {
    if (!connectionKey) return null;
    return E2Data.WithConnectionKey(apiRequestAddress, connectionKey, "EU_SCR")
      .ApplyParams({ filter: "Valid=true", orderBy: "Name" });
  }, [connectionKey]);

  const { data: stepsQueryData, isLoading: stepsLoading } = useQuery({
    queryKey: ["workflowSteps", connectionKey],
    queryFn: () => stepsQueryInstance.GetData(),
    enabled: !!connectionKey && !!stepsQueryInstance,
    staleTime: Infinity,
  });

  const { data: scrQueryData } = useQuery({
    queryKey: ["scrData", connectionKey],
    queryFn: () => scrQueryInstance.GetData(),
    enabled: !!connectionKey && !!scrQueryInstance,
    staleTime: Infinity,
  });

  const { data: docupathExtractionData, isLoading: docupathExtractionLoading } = useQuery({
    queryKey: ["serverFlag_docupathExtraction"],
    queryFn: async () => {
      const ddd = new DDD(apiRequestAddress);
      return ddd.Rpc_ExecuteMethod("EUeInvoices.GetSettingValue", { VarName: "DocupathExtraction" });
    },
    enabled: !!connectionKey,
    staleTime: Infinity,
  });

  const { data: mistralExtractionData, isLoading: mistralExtractionLoading } = useQuery({
    queryKey: ["serverFlag_mistralExtraction"],
    queryFn: async () => {
      const ddd = new DDD(apiRequestAddress);
      return ddd.Rpc_ExecuteMethod("EUeInvoices.GetSettingValue", { VarName: "MistralExtraction" });
    },
    enabled: !!connectionKey,
    staleTime: Infinity,
  });

  const isSettingTrue = (data) => data?.result?.Result === true || data?.result?.Result === "true";
  const isOldExtractionAllowed = isSettingTrue(docupathExtractionData);
  const isNewExtractionAllowed = isSettingTrue(mistralExtractionData);
  const extractionSettingsLoading = docupathExtractionLoading || mistralExtractionLoading;

  const [extractionMode, setExtractionMode] = useState(null); // "old" | "new"
  useEffect(() => {
    if (extractionSettingsLoading || extractionMode !== null) return;
    if (isOldExtractionAllowed && !isNewExtractionAllowed) setExtractionMode("old");
    else setExtractionMode("new");
  }, [extractionSettingsLoading, isOldExtractionAllowed, isNewExtractionAllowed, extractionMode]);

  const canSwitchExtractionMode = !extractionSettingsLoading && isOldExtractionAllowed && isNewExtractionAllowed;

  const rawSteps = stepsQueryData?.result?.data || [];
  const scrList = scrQueryData?.result?.data || [];

  const activeSCR = useMemo(
    () => scrList.find((r) => mainOrgCountry?.toLowerCase() === r.CountryCode?.toLowerCase()),
    [scrList, mainOrgCountry]
  );

  const isStepVisible = useCallback((stepNum) => {
    if (!activeSCR?.MeaningfulSteps) return true;
    try {
      const info = JSON.parse(activeSCR.MeaningfulSteps);
      if (info.supported?.some((s) => (typeof s === "object" ? s.step == stepNum : s == stepNum))) return true;
      for (const s of info.unsupported) {
        if (typeof s === "object" ? s.step == stepNum : stepNum == s) return true;
      }
    } catch {
      return false;
    }
    return false;
  }, [activeSCR]);

  const isStepEnabled = useCallback((stepNum) => {
    if (!activeSCR?.MeaningfulSteps) return { enabled: true, enabledMessage: null };
    try {
      const info = JSON.parse(activeSCR.MeaningfulSteps);
      if (info.supported?.some((s) => (typeof s === "object" ? s.step == stepNum : s == stepNum))) {
        const found = info.supported.find((s) => (typeof s === "object" ? s.step == stepNum : s == stepNum));
        return (typeof found === "object" && found.reason)
          ? { enabled: true, enabledMessage: found.reason }
          : { enabled: true, enabledMessage: null };
      }
      for (const s of info.unsupported) {
        if (typeof s === "object") {
          if (s.step == stepNum) return { enabled: false, enabledMessage: s.reason || "Coming soon!" };
        } else {
          if (stepNum == s) return { enabled: false, enabledMessage: "Coming soon!" };
        }
      }
    } catch {
      return { enabled: false, enabledMessage: null };
    }
    return { enabled: false, enabledMessage: null };
  }, [activeSCR]);

  const steps = useMemo(
    () => rawSteps
      .filter((s) => isStepVisible(s.Step))
      .map((s) => ({ ...s, ...isStepEnabled(s.Step) })),
    [rawSteps, isStepVisible, isStepEnabled]
  );

  const addFiles = useCallback((incoming) => {
    const pdfs = Array.from(incoming).filter((f) => f.type === "application/pdf");
    setFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...pdfs.filter((f) => !existing.has(f.name + f.size))];
    });
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleInputChange = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const toggleGlobalStep = (stepNum) =>
    setGlobalSteps((prev) =>
      prev.includes(stepNum) ? prev.filter((s) => s !== stepNum) : [...prev, stepNum]
    );

  const togglePerInvoiceStep = (fileName, stepNum) =>
    setPerInvoiceSteps((prev) => {
      const current = prev[fileName] || [];
      return {
        ...prev,
        [fileName]: current.includes(stepNum)
          ? current.filter((s) => s !== stepNum)
          : [...current, stepNum],
      };
    });

  const handleObjectTextChange = (fileName, text) => {
    setObjectEdits((prev) => ({ ...prev, [fileName]: text }));
    try {
      JSON.parse(text);
      setObjectEditErrors((prev) => ({ ...prev, [fileName]: null }));
    } catch (e) {
      setObjectEditErrors((prev) => ({ ...prev, [fileName]: "Invalid JSON: " + e.message }));
    }
  };

  const executeSavePhase = async (phase1, getStepsForItem, getObjectForItem = (name, obj) => obj) => {
    const saveSettled = await Promise.allSettled(
      phase1.map((r) =>
        r._saveObject
          ? saveInvoice(getObjectForItem(r.name, r._saveObject), connectionKey, getStepsForItem(r.name))
          : Promise.resolve(null)
      )
    );

    setResults(
      phase1.map(({ _saveObject, ...r }, i) => {
        if (!_saveObject) return r;
        const outcome = saveSettled[i];
        if (outcome.status === "fulfilled" && outcome.value !== null) {
          const res = outcome.value;
          const isSaveError = res?.error || res?.result?.Status === "Error";
          return {
            ...r,
            save: {
              status: isSaveError ? STATUS.ERROR : STATUS.SUCCESS,
              data: res?.result ?? null,
              error: isSaveError ? (res?.error?.message || res?.result?.Reason || "Save failed") : null,
            },
          };
        }
        return {
          ...r,
          save: { status: STATUS.ERROR, data: null, error: outcome.reason?.message || "Save request failed" },
        };
      })
    );

    setIsExtracting(false);
  };

  const handleExtract = async () => {
    if (!files.length || isExtracting) return;
    setIsExtracting(true);
    setResults(files.map((f) => ({ name: f.name, status: STATUS.LOADING, data: null, error: null, save: null, saveable: false })));

    const docupathMode = extractionMode === "old" ? await getDocupathMode() : undefined;
    const extractSettled = await Promise.allSettled(
      files.map((f) => extractionMode === "new" ? extractFileNew(f) : extractFileOld(f, docupathMode))
    );

    const phase1 = files.map((f, i) => {
      const outcome = extractSettled[i];
      if (outcome.status === "fulfilled") {
        const res = outcome.value;
        const isError = res?.error
          || res?.result?.Result?.Status?.toUpperCase() === "ERROR"
          || res?.result?.Status?.toUpperCase() === "ERROR";
        const hasObject = !isError && res?.result?.Result?.Object;
        return {
          name: f.name,
          status: isError ? STATUS.ERROR : STATUS.SUCCESS,
          data: isError ? null : res?.result,
          error: isError ? (res?.error?.message || res?.result?.Result?.Reason || res?.result?.Reason || "Extraction failed") : null,
          save: hasObject ? { status: STATUS.LOADING, data: null, error: null } : null,
          saveable: !!hasObject,
          _saveObject: hasObject ? res.result.Result.Object : null,
        };
      }
      return { name: f.name, status: STATUS.ERROR, data: null, error: outcome.reason?.message || "Request failed", save: null, saveable: false, _saveObject: null };
    });

    if (stepsMode === "individual") {
      phase1Ref.current = phase1;
      setResults(phase1.map((r) => ({ ...r, save: null })));
      setIsExtracting(false);
      setAwaitingStepConfirm(phase1.some(r => r.saveable));
      return;
    }

    setResults(phase1.map(({ _saveObject, ...r }) => r));
    await executeSavePhase(phase1, () => stepsMode === "all" ? globalSteps : []);
  };

  const handleConfirmSteps = async () => {
    const stepsSnapshot = { ...perInvoiceSteps };
    const objectsSnapshot = { ...objectEdits };
    setAwaitingStepConfirm(false);
    setIsExtracting(true);
    setResults((prev) =>
      prev.map((r) => r.saveable ? { ...r, save: { status: STATUS.LOADING, data: null, error: null } } : r)
    );
    await executeSavePhase(
      phase1Ref.current,
      (name) => stepsSnapshot[name] || [],
      (name, original) => {
        const text = objectsSnapshot[name];
        if (text === undefined) return original;
        try { return JSON.parse(text); } catch { return original; }
      }
    );
  };

  const handleGoBack = () => {
    setAwaitingStepConfirm(false);
    setResults([]);
    setPerInvoiceSteps({});
    setObjectEdits({});
    setObjectEditErrors({});
    phase1Ref.current = null;
  };

  const handleReset = () => {
    setFiles([]);
    setResults([]);
    setAwaitingStepConfirm(false);
    setPerInvoiceSteps({});
    setObjectEdits({});
    setObjectEditErrors({});
    phase1Ref.current = null;
    if (inputRef.current) inputRef.current.value = "";
  };

  const hasResults = results.length > 0;
  const allDone = hasResults && !isExtracting && !awaitingStepConfirm;
  const showStepsConfig = files.length > 0 && !hasResults && !isExtracting;
  const hasObjectErrors = Object.values(objectEditErrors).some(Boolean);
  const extractionModeUnknown = extractionSettingsLoading || extractionMode === null;

  return (
    <>
      <Helmet>
        <title>AI Extraction Studio | DDD Invoices</title>
      </Helmet>
      <div className={classes.container}>
        <div className={classes.header}>
          <div>
            <Typography variant="h5" className={classes.title}>
              AI Extraction Studio
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              Drop your issued invoices below. We'll extract, validate and route
              them straight to Issued Invoices in the Invoice Flow Control.
            </Typography>
          </div>
          <ExtractionModeToggle
            mode={extractionMode || "new"}
            onChange={setExtractionMode}
            canSwitch={canSwitchExtractionMode}
            loading={extractionSettingsLoading || extractionMode === null}
          />
        </div>

        <div className={classes.layout}>
          {/* Drop zone — shrinks when results are showing */}
          <div className={`${classes.dropZoneWrapper} ${hasResults ? classes.compact : ""}`}>
            <div
              className={`${classes.dropZone} ${isDragging ? classes.dragging : ""} ${hasResults ? classes.compactZone : ""}`}
              onClick={() => !isExtracting && !awaitingStepConfirm && inputRef.current?.click()}
              onDrop={!isExtracting && !awaitingStepConfirm ? handleDrop : undefined}
              onDragOver={!isExtracting && !awaitingStepConfirm ? handleDragOver : undefined}
              onDragLeave={handleDragLeave}
            >
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                multiple
                className={classes.hiddenInput}
                onChange={handleInputChange}
              />

              {files.length === 0 ? (
                <div className={classes.dropContent}>
                  <CloudUploadIcon className={classes.uploadIcon} />
                  <Typography variant="h6" className={classes.dropTitle}>
                    Drop PDF files here
                  </Typography>
                  <Typography variant="body2" className={classes.dropHint}>
                    or <span className={classes.browseLink}>click to browse</span>
                  </Typography>
                  <div className={classes.badge}>
                    <PictureAsPdfIcon fontSize="small" />
                    <span>PDF only &nbsp;·&nbsp; Multiple files supported</span>
                  </div>
                </div>
              ) : (
                <div className={classes.fileList} onClick={(e) => e.stopPropagation()}>
                  <div className={classes.fileListHeader}>
                    <Typography variant="subtitle2" className={classes.fileCount}>
                      {files.length} file{files.length !== 1 ? "s" : ""} selected
                    </Typography>
                    {!isExtracting && !hasResults && !awaitingStepConfirm && (
                      <button
                        className={classes.addMoreBtn}
                        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                      >
                        + Add more
                      </button>
                    )}
                  </div>
                  <div className={classes.fileGrid}>
                    {files.map((file, i) => (
                      <div key={i} className={classes.fileChip} onMouseEnter={(e) => showPreview(e, file)} onMouseLeave={hidePreview}>
                        {results[i]?.status === STATUS.LOADING ? (
                          <CircularProgress size={14} thickness={5} className={classes.spinner} />
                        ) : results[i]?.status === STATUS.SUCCESS ? (
                          <CheckCircleIcon className={classes.successIcon} fontSize="small" />
                        ) : results[i]?.status === STATUS.ERROR ? (
                          <ErrorIcon className={classes.errorIcon} fontSize="small" />
                        ) : (
                          <PictureAsPdfIcon className={classes.pdfIcon} fontSize="small" />
                        )}
                        <span className={classes.fileName}>{file.name}</span>
                        <span className={classes.fileSize}>{(file.size / 1024).toFixed(0)} KB</span>
                        {!isExtracting && !hasResults && !awaitingStepConfirm && (
                          <IconButton size="small" className={classes.removeBtn} onClick={() => removeFile(i)}>
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {showStepsConfig && (
              <StepsConfig
                steps={steps}
                stepsLoading={stepsLoading}
                mode={stepsMode}
                onModeChange={setStepsMode}
                selectedSteps={globalSteps}
                onStepToggle={toggleGlobalStep}
              />
            )}

            <div className={classes.actions}>
              {files.length > 0 && !hasResults && !awaitingStepConfirm && (
                <button
                  className={classes.uploadBtn}
                  onClick={handleExtract}
                  disabled={isExtracting || extractionModeUnknown}
                >
                  {isExtracting ? (
                    <><CircularProgress size={14} thickness={5} style={{ color: "white", marginRight: 8 }} />Extracting…</>
                  ) : (
                    <>Extract &amp; Route {files.length} invoice{files.length !== 1 ? "s" : ""}</>
                  )}
                </button>
              )}
              {awaitingStepConfirm && (
                <>
                  <button className={classes.resetBtn} onClick={handleGoBack} style={{ marginRight: "0.6rem" }}>
                    Go back
                  </button>
                  <button className={classes.uploadBtn} onClick={handleConfirmSteps} disabled={hasObjectErrors}>
                    Apply Steps &amp; Save
                  </button>
                </>
              )}
              {allDone && (
                <button className={classes.resetBtn} onClick={handleReset}>
                  Upload more invoices
                </button>
              )}
              {allDone && isMaintainer && (
                <button className={classes.uploadBtn} onClick={handleExtract} disabled={extractionModeUnknown}>
                  Re-run
                </button>
              )}
            </div>
          </div>

          {/* Results panel */}
          {hasResults && (
            <div className={classes.resultsPanel}>
              <Typography variant="subtitle2" className={classes.resultsTitle}>
                Results
              </Typography>
              <div className={classes.resultsList}>
                {results.map((r, i) => (
                  <ResultCard
                    key={i}
                    result={r}
                    awaitingStepConfirm={awaitingStepConfirm}
                    steps={steps}
                    selectedSteps={perInvoiceSteps[r.name] || []}
                    onToggleStep={(stepNum) => togglePerInvoiceStep(r.name, stepNum)}
                    objectText={objectEdits[r.name]}
                    objectEditError={objectEditErrors[r.name]}
                    onObjectTextChange={(text) => handleObjectTextChange(r.name, text)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {previewUrl && (
        <div
          className={classes.pdfPreviewPopup}
          style={{ top: previewPos.top, left: previewPos.left }}
          onMouseEnter={cancelHidePreview}
          onMouseLeave={hidePreview}
        >
          <iframe src={`${previewUrl}#toolbar=0`} title="PDF preview" className={classes.pdfPreviewFrame} />
        </div>
      )}
    </>
  );
};

// ── ResultCard ────────────────────────────────────────────────────────────────

const ResultCard = ({ result, awaitingStepConfirm, steps, selectedSteps, onToggleStep, objectText, objectEditError, onObjectTextChange }) => {
  const [extractExpanded, setExtractExpanded] = useState(false);
  const [saveExpanded, setSaveExpanded] = useState(false);
  const [objectExpanded, setObjectExpanded] = useState(false);

  const hasError = result.status === STATUS.ERROR || result.save?.status === STATUS.ERROR;
  const allOk = result.status === STATUS.SUCCESS && (!result.save || result.save.status === STATUS.SUCCESS);

  const extractHasJson = result.status === STATUS.SUCCESS && result.data;
  const saveHasJson = result.save?.status === STATUS.SUCCESS && result.save.data;

  return (
    <div className={`${classes.resultCard} ${allOk ? classes.resultSuccess : hasError ? classes.resultError : ""}`}>
      <div className={classes.resultHeader}>
        {result.status === STATUS.LOADING && <CircularProgress size={14} thickness={5} className={classes.spinner} />}
        {result.status === STATUS.SUCCESS && <CheckCircleIcon className={classes.successIcon} fontSize="small" />}
        {result.status === STATUS.ERROR && <ErrorIcon className={classes.errorIcon} fontSize="small" />}
        <span className={classes.resultName}>{result.name}</span>
      </div>

      {/* Extraction step */}
      <div className={classes.stepSection}>
        <div
          className={`${classes.stepHeader} ${extractHasJson ? classes.stepHeaderClickable : ""}`}
          onClick={extractHasJson ? () => setExtractExpanded(v => !v) : undefined}
        >
          <div className={classes.stepHeaderLeft}>
            {result.status === STATUS.LOADING && <CircularProgress size={11} thickness={5} className={classes.spinner} />}
            {result.status === STATUS.SUCCESS && <CheckCircleIcon className={classes.successIcon} style={{ fontSize: 14 }} />}
            {result.status === STATUS.ERROR && <ErrorIcon className={classes.errorIcon} style={{ fontSize: 14 }} />}
            <span className={classes.stepLabel}>Extraction</span>
          </div>
          {extractHasJson && (
            <KeyboardArrowDownIcon className={`${classes.chevron} ${extractExpanded ? classes.chevronOpen : ""}`} />
          )}
        </div>
        {result.status === STATUS.ERROR && (
          <p className={classes.errorMsg}>{result.error}</p>
        )}
        {extractHasJson && extractExpanded && (
          <pre className={classes.resultPre}>{JSON.stringify(result.data, null, 2)}</pre>
        )}
      </div>

      {/* Per-invoice step picker — shown when awaiting confirmation and extraction succeeded */}
      {awaitingStepConfirm && result.saveable && steps.length > 0 && (
        <div className={classes.stepSection}>
          <div className={classes.stepHeaderLeft} style={{ marginBottom: "0.4rem" }}>
            <span className={classes.stepLabel}>Select steps</span>
          </div>
          <StepPicker steps={steps} selected={selectedSteps} onToggle={onToggleStep} />
        </div>
      )}

      {/* Editable invoice object — lets the user fix extraction misses before saving */}
      {awaitingStepConfirm && result.saveable && (
        <div className={classes.stepSection}>
          <div
            className={`${classes.stepHeader} ${classes.stepHeaderClickable}`}
            onClick={() => setObjectExpanded(v => !v)}
          >
            <div className={classes.stepHeaderLeft}>
              <span className={classes.stepLabel}>Edit invoice data</span>
              {objectEditError && <ErrorIcon className={classes.errorIcon} style={{ fontSize: 14 }} />}
            </div>
            <KeyboardArrowDownIcon className={`${classes.chevron} ${objectExpanded ? classes.chevronOpen : ""}`} />
          </div>
          {objectExpanded && (
            <>
              <textarea
                className={classes.objectEditor}
                value={objectText !== undefined ? objectText : JSON.stringify(result._saveObject, null, 2)}
                onChange={(e) => onObjectTextChange(e.target.value)}
                spellCheck={false}
              />
              {objectEditError && <p className={classes.errorMsg}>{objectEditError}</p>}
            </>
          )}
        </div>
      )}

      {/* Save step — only rendered when extraction produced an Object */}
      {result.save !== null && (
        <div className={classes.stepSection}>
          <div
            className={`${classes.stepHeader} ${saveHasJson ? classes.stepHeaderClickable : ""}`}
            onClick={saveHasJson ? () => setSaveExpanded(v => !v) : undefined}
          >
            <div className={classes.stepHeaderLeft}>
              {result.save.status === STATUS.LOADING && <CircularProgress size={11} thickness={5} className={classes.spinner} />}
              {result.save.status === STATUS.SUCCESS && <CheckCircleIcon className={classes.successIcon} style={{ fontSize: 14 }} />}
              {result.save.status === STATUS.ERROR && <ErrorIcon className={classes.errorIcon} style={{ fontSize: 14 }} />}
              <span className={classes.stepLabel}>Save to Invoices</span>
            </div>
            {saveHasJson && (
              <KeyboardArrowDownIcon className={`${classes.chevron} ${saveExpanded ? classes.chevronOpen : ""}`} />
            )}
          </div>
          {result.save.status === STATUS.ERROR && (
            <p className={classes.errorMsg}>{result.save.error}</p>
          )}
          {saveHasJson && saveExpanded && (
            <pre className={`${classes.resultPre} ${classes.resultPreSuccess}`}>{JSON.stringify(result.save.data, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default AIExtractionStudio;
